import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Rating,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import ReplyIcon from '@mui/icons-material/Reply';
import PageTitleWrapper from '@/components/PageTitleWrapper';
import { useUserViewMode } from '@/hooks/useUserViewMode';
import { headerBranchId } from '../food-delivery/helpers';
import { branchService } from '@/data/branchService';
import { foodDeliveryService } from '@/data/foodDeliveryService';
import {
  FOOD_PROVIDER_LABELS,
  FoodIntegration,
  ProviderRatingSummary,
  ProviderReview,
  REVIEW_ANSWER_STATUS_COLOR,
  REVIEW_ANSWER_STATUS_LABELS
} from '@/types/FoodDelivery.interface';
import { Branch } from '@/types/Branch.interface';
import FoodDeliveryEmptyState from '../food-delivery/components/FoodDeliveryEmptyState';

type Toast = { msg: string; severity: 'success' | 'error' | 'info' } | null;

const fmt = (v?: string) => (v ? new Date(v).toLocaleString('tr-TR') : '—');

const ScoreLine: React.FC<{ label: string; value?: number }> = ({
  label,
  value
}) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    <Typography variant="body2" sx={{ minWidth: 72 }}>
      {label}
    </Typography>
    <Rating value={value ?? 0} precision={0.1} readOnly size="small" />
    <Typography variant="body2" color="text.secondary">
      {value != null ? value.toFixed(2) : '—'}
    </Typography>
  </Stack>
);

/**
 * Restoran değerlendirmeleri (plan Faz 5).
 *
 * Yorumlar panelde saklanmaz; her açılışta platformdan okunur. Sebep: yorum
 * sonradan değiştirilemez ama restoran cevabı ONAY sürecinden geçer ve
 * reddedilebilir — yerel kopya, yayınlanmamış bir cevabı yayınlanmış gösterirdi.
 */
const FoodDeliveryReviews: React.FC = () => {
  const { isBranchAdmin, isAdminView, currentBranch } = useUserViewMode();

  const [branches, setBranches] = useState<Branch[]>([]);
  const headerBranch = headerBranchId(currentBranch, isAdminView);
  const [branchId, setBranchId] = useState<number | null>(headerBranch);
  // Üst menüden şube değişince sayfa da o şubeye geçer.
  useEffect(() => {
    if (headerBranch) setBranchId(headerBranch);
  }, [headerBranch]);
  const [integrations, setIntegrations] = useState<FoodIntegration[]>([]);
  const [integrationId, setIntegrationId] = useState<number | null>(null);

  const [rating, setRating] = useState<ProviderRatingSummary | null>(null);
  const [reviews, setReviews] = useState<ProviderReview[]>([]);
  const [onlyWithComment, setOnlyWithComment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<Toast>(null);

  const [replyTarget, setReplyTarget] = useState<ProviderReview | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyBusy, setReplyBusy] = useState(false);

  useEffect(() => {
    if (isBranchAdmin) return;
    branchService
      .getAllFlat({ getAll: true })
      .then(setBranches)
      .catch(() =>
        setToast({ msg: 'Şubeler getirilemedi.', severity: 'error' })
      );
  }, [isBranchAdmin]);

  useEffect(() => {
    if (!branchId) {
      setIntegrations([]);
      setIntegrationId(null);
      return;
    }
    foodDeliveryService
      .listByBranch(branchId)
      .then((list) => {
        setIntegrations(list);
        setIntegrationId(list[0]?.id ?? null);
      })
      .catch(() =>
        setToast({ msg: 'Entegrasyonlar getirilemedi.', severity: 'error' })
      );
  }, [branchId]);

  const load = useCallback(async () => {
    if (!integrationId) {
      setRating(null);
      setReviews([]);
      return;
    }
    setLoading(true);
    try {
      const [r, list] = await Promise.all([
        foodDeliveryService
          .getReviewRating(integrationId)
          // Puan özeti alınamazsa yorumlar yine gösterilmeli.
          .catch(() => null),
        foodDeliveryService.listReviews(integrationId, {
          ...(onlyWithComment ? { hasComment: true } : {})
        })
      ]);
      setRating(r);
      setReviews(list);
    } catch (err) {
      setToast({
        msg: `Değerlendirmeler alınamadı: ${
          (err as { response?: { data?: { message?: string } } }).response?.data
            ?.message ?? 'bilinmeyen hata'
        }`,
        severity: 'error'
      });
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [integrationId, onlyWithComment]);

  useEffect(() => {
    load();
  }, [load]);

  const confirmReply = async () => {
    if (!replyTarget || !integrationId) return;
    setReplyBusy(true);
    try {
      await foodDeliveryService.replyToReview(
        integrationId,
        replyTarget.reviewId,
        replyText.trim()
      );
      setToast({
        msg: 'Cevabınız gönderildi; platform onayından sonra yayınlanır.',
        severity: 'success'
      });
      setReplyTarget(null);
      setReplyText('');
      await load();
    } catch (err) {
      setToast({
        msg: `Cevap gönderilemedi: ${
          (err as { response?: { data?: { message?: string } } }).response?.data
            ?.message ?? 'bilinmeyen hata'
        }`,
        severity: 'error'
      });
    } finally {
      setReplyBusy(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <PageTitleWrapper>
        <Box>
          <Typography variant="h3" component="h3" gutterBottom>
            Restoran Değerlendirmeleri
          </Typography>
          <Typography variant="subtitle2">
            Müşteri puanlarını ve yorumlarını görün, yorumlara cevap yazın.
            Müşteriler siparişten sonra 7 gün içinde değerlendirme yapabilir.
          </Typography>
        </Box>
      </PageTitleWrapper>

      <Box sx={{ px: 4, pb: 4 }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ md: 'center' }}
            >
              {!isBranchAdmin && (
                <Autocomplete
                  sx={{ minWidth: 260 }}
                  options={branches}
                  getOptionLabel={(b) => b.name}
                  value={branches.find((b) => b.id === branchId) ?? null}
                  onChange={(_e, val) => setBranchId(val?.id ?? null)}
                  renderInput={(params) => (
                    <TextField {...params} label="Şube seç" />
                  )}
                />
              )}
              <Autocomplete
                sx={{ minWidth: 260 }}
                options={integrations}
                getOptionLabel={(i) =>
                  `${FOOD_PROVIDER_LABELS[i.providerCode]}${
                    i.displayName ? ` — ${i.displayName}` : ''
                  }`
                }
                value={integrations.find((i) => i.id === integrationId) ?? null}
                onChange={(_e, val) => setIntegrationId(val?.id ?? null)}
                disabled={!branchId}
                renderInput={(params) => (
                  <TextField {...params} label="Entegrasyon seç" />
                )}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={onlyWithComment}
                    onChange={(e) => setOnlyWithComment(e.target.checked)}
                  />
                }
                label="Yalnız yorumu olanlar"
              />
            </Stack>
          </CardContent>
        </Card>

        {!integrationId ? (
          branchId && integrations.length === 0 ? (
            <FoodDeliveryEmptyState
              title="Entegrasyon yok"
              message="Bu şubede yemek platformu entegrasyonu yok; değerlendirme de gelmez."
            />
          ) : (
            <Alert severity="info">
              Değerlendirmeler için bir şube ve entegrasyon seçin.
            </Alert>
          )
        ) : loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h4" gutterBottom>
                  Puan Özeti
                </Typography>
                {!rating ? (
                  <Alert severity="info">Puan özeti alınamadı.</Alert>
                ) : !rating.available ? (
                  <Alert severity="info">
                    {rating.unavailableReason ??
                      'Puan ortalaması için yeterli değerlendirme yok.'}
                  </Alert>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="h2">
                        {rating.overall?.toFixed(2) ?? '—'}
                      </Typography>
                      <Rating
                        value={rating.overall ?? 0}
                        precision={0.1}
                        readOnly
                      />
                      <Typography variant="caption" display="block">
                        {rating.ratingCount ?? 0} değerlendirme ·{' '}
                        {rating.commentCount ?? 0} yorum
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Stack spacing={1}>
                        <ScoreLine label="Lezzet" value={rating.flavor} />
                        <ScoreLine label="Servis" value={rating.service} />
                        <ScoreLine label="Teslimat" value={rating.delivery} />
                      </Stack>
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h4" gutterBottom>
                  Değerlendirmeler ({reviews.length})
                </Typography>
                {reviews.length === 0 ? (
                  <Alert severity="info">
                    Bu kriterlerde değerlendirme yok.
                  </Alert>
                ) : (
                  <Stack spacing={2} divider={<Divider flexItem />}>
                    {reviews.map((review) => (
                      <Box key={review.reviewId}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          flexWrap="wrap"
                          gap={1}
                        >
                          <Box>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Rating
                                value={review.overallScore ?? 0}
                                readOnly
                                size="small"
                              />
                              <Typography variant="body2" fontWeight={600}>
                                {review.overallScore ?? '—'}
                              </Typography>
                              {review.orderNumber && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`Sipariş ${review.orderNumber}`}
                                />
                              )}
                            </Stack>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {fmt(review.createdAt)}
                              {review.productNames.length > 0 &&
                                ` · ${review.productNames.join(', ')}`}
                            </Typography>
                          </Box>
                          {!review.answer && (
                            <Button
                              size="small"
                              startIcon={<ReplyIcon fontSize="small" />}
                              onClick={() => {
                                setReplyText('');
                                setReplyTarget(review);
                              }}
                            >
                              Cevap yaz
                            </Button>
                          )}
                        </Stack>

                        <Typography variant="body1" sx={{ mt: 1 }}>
                          {review.comment ?? (
                            <em>Yorum yazılmamış (yalnız puan verilmiş).</em>
                          )}
                        </Typography>

                        {review.answer && (
                          <Box
                            sx={{
                              mt: 1,
                              pl: 2,
                              borderLeft: '3px solid',
                              borderColor: 'divider'
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2" fontWeight={600}>
                                Cevabınız
                              </Typography>
                              {review.answer.status && (
                                <Chip
                                  size="small"
                                  color={
                                    REVIEW_ANSWER_STATUS_COLOR[
                                      review.answer.status
                                    ] ?? 'default'
                                  }
                                  label={
                                    REVIEW_ANSWER_STATUS_LABELS[
                                      review.answer.status
                                    ] ?? review.answer.status
                                  }
                                />
                              )}
                            </Stack>
                            <Typography variant="body2">
                              {review.answer.text}
                            </Typography>
                            {review.answer.rejectedReason && (
                              <Typography variant="caption" color="error">
                                Red nedeni: {review.answer.rejectedReason}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Box>

      <Dialog
        open={!!replyTarget}
        onClose={() => setReplyTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Yoruma cevap yaz</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            “{replyTarget?.comment ?? 'Yorum yok'}”
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Cevabınız"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            inputProps={{ maxLength: 1000 }}
            helperText="Cevabınız platform onayından sonra yayınlanır. Uygunsuz bulunursa reddedilebilir."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplyTarget(null)}>Vazgeç</Button>
          <Button
            variant="contained"
            disabled={replyBusy || replyText.trim().length < 2}
            onClick={confirmReply}
          >
            Gönder
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            onClose={() => setToast(null)}
            variant="filled"
          >
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default FoodDeliveryReviews;
